import { userError } from "@/lib/userError";
import React, { useEffect, useRef, useState } from "react";
import { Bell, BellRing, Plus, Repeat, Check, Sunrise } from "lucide-react";
import {
  getReminders, getReminderItem, createReminder, toggleReminder, deleteReminder,
  getMorningBriefingPreferences, getPushConfig, updateMorningBriefingPreferences, updateNotificationPrivacy,
} from "@/lib/apiClient";
import { useData } from "@/lib/useData";
import { useLinkedRecord } from "@/lib/useLinkedRecord";
import { PRIVATE_NOTIFICATIONS_KEY } from "@/components/ReminderNotifier";
import { PageHeader, LoadingCards, ErrorState, EmptyState, RefreshNotice } from "@/components/Common";
import { RECURRENCE, fiDateTime } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { nativeAlarmAvailable, syncNativeMorningAlarm } from "@/lib/nativeAlarm";
import {
  disableReminderNotifications,
  reminderNotificationsEnabled,
  requestReminderNotifications,
} from "@/components/ReminderNotifier";
import { ConfirmDeleteButton } from "@/components/ConfirmDeleteButton";
import { requestKeyFor, resetRequestKey } from "@/lib/idempotency";
import { DateShortcuts } from "@/components/DateShortcuts";
import { EditItemDialog } from "@/components/EditItemDialog";

const WEEKDAYS = [
  { value: 1, label: "Ma" }, { value: 2, label: "Ti" },
  { value: 3, label: "Ke" }, { value: 4, label: "To" },
  { value: 5, label: "Pe" }, { value: 6, label: "La" },
  { value: 7, label: "Su" },
];

const REMINDER_FILTERS = [
  { id: "active", label: "Avoimet" },
  { id: "done", label: "Hoidetut" },
  { id: "all", label: "Kaikki" },
];

const REMINDER_EMPTY_COPY = {
  active: ["Ei avoimia muistutuksia", "Luo uusi muistutus, kun haluat ACAI:n muistuttavan asiasta."],
  done: ["Ei hoidettuja muistutuksia", "Kuitatut kertamuistutukset näkyvät täällä."],
  all: ["Ei muistutuksia", "Luo ensimmäinen muistutus."],
};

const reminderState = (reminder) => {
  if (reminder.done) return "done";
  const remindAt = new Date(reminder.remind_at);
  if (Number.isNaN(remindAt.getTime())) return "unknown";
  const now = new Date();
  if (remindAt.getTime() <= now.getTime()) return "overdue";
  if (
    remindAt.getFullYear() === now.getFullYear() &&
    remindAt.getMonth() === now.getMonth() &&
    remindAt.getDate() === now.getDate()
  ) return "today";
  return "future";
};

const reminderOrder = (left, right) => {
  const rank = { overdue: 0, today: 1, future: 2, unknown: 3, done: 4 };
  const stateDiff = rank[reminderState(left)] - rank[reminderState(right)];
  if (stateDiff) return stateDiff;
  if (left.done && right.done) {
    return new Date(right.completed_at || 0).getTime() - new Date(left.completed_at || 0).getTime();
  }
  return new Date(left.remind_at).getTime() - new Date(right.remind_at).getTime();
};

const NewReminderDialog = ({ onCreated, testid = "new-reminder-btn", triggerLabel = "Uusi muistutus" }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [when, setWhen] = useState("");
  const [recurrence, setRecurrence] = useState("none");
  const [saving, setSaving] = useState(false);
  const formId = React.useId();
  const [formError, setFormError] = useState("");
  const [invalidField, setInvalidField] = useState("");
  const requestRef = useRef({ key: "", fingerprint: "" });

  const submit = async () => {
    if (!title.trim() || !when) { setFormError("Täytä aihe ja ajankohta."); setInvalidField(formId + "-field0"); document.getElementById(formId + "-field0")?.focus(); return; }
    setSaving(true);
    try {
      const payload = { title, remind_at: new Date(when).toISOString(), recurrence };
      await createReminder({ ...payload, idempotency_key: requestKeyFor(requestRef, payload) });
      toast.success("Muistutus luotu.");
      resetRequestKey(requestRef);
      setOpen(false); setTitle(""); setWhen(""); setRecurrence("none");
      await onCreated();
    } catch (requestError) {
      toast.error(userError(requestError, "Muistutuksen luominen epäonnistui."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" data-testid={testid} className="inline-flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground active:scale-95 transition-transform">
          <Plus className="h-4 w-4" /> {triggerLabel}
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl max-w-sm mx-auto" data-testid="new-reminder-dialog">
        <DialogHeader><DialogTitle className="font-heading">Uusi muistutus</DialogTitle></DialogHeader>
        <form id={formId} onSubmit={(event) => { event.preventDefault(); setFormError(""); submit(); }} onChange={() => { setFormError(""); setInvalidField(""); }} onInvalidCapture={(event) => { event.preventDefault(); if (event.target === event.currentTarget.querySelector(":invalid")) { setInvalidField(event.target.id); setFormError("Tarkista kenttä: " + (event.target.labels?.[0]?.textContent || "pakollinen tieto")); event.target.focus(); } }} className="space-y-3">
          <div><Label htmlFor={formId + "-field0"} className="text-xs">Aihe</Label><Input id={formId + "-field0"} aria-invalid={invalidField === formId + "-field0"} aria-describedby={formId + "-error"} required data-testid="reminder-title" value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-xl mt-1" /></div>
          <div><Label htmlFor={formId + "-field1"} className="text-xs">Ajankohta</Label><Input id={formId + "-field1"} aria-invalid={invalidField === formId + "-field1"} aria-describedby={formId + "-error"} required data-testid="reminder-time" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="rounded-xl mt-1" /><DateShortcuts value={when} onChange={setWhen} withTime /></div>
          <div>
            <Label htmlFor={formId + "-recurrence"} className="text-xs">Toistuvuus</Label>
            <Select value={recurrence} onValueChange={setRecurrence}>
              <SelectTrigger id={formId + "-recurrence"} data-testid="reminder-recurrence" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(RECURRENCE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </form>
        <p id={formId + "-error"} role="alert" className="text-sm text-destructive">{formError}</p>
        <DialogFooter>
          <button type="submit" form={formId} data-testid="reminder-create-confirm" disabled={saving} className="w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">{saving ? "Tallennetaan…" : "Luo muistutus"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Reminders() {
  const hasNativeAlarm = nativeAlarmAvailable();
  const { data, loading, error, refetch, refreshing, updatedAt, setData } = useData(getReminders, [], ["reminders"]);
  const selection = useLinkedRecord("reminder", getReminderItem, "reminders");
  const targetId = selection.id;
  const [removedIds, setRemovedIds] = useState(() => new Set());
  const selected = !selection.error && !removedIds.has(targetId) ? selection.data : null;
  const selectedId = selected?.id;
  const refreshReminders = async () => {
    await Promise.all([refetch(), selection.refetch()]);
  };
  const [morningLoad, setMorningLoad] = useState("loading");
  const [privateContent, setPrivateContent] = useState(true);
  const [privacySaving, setPrivacySaving] = useState(false);
  const [filter, setFilter] = useState("active");
  const [updatingReminder, setUpdatingReminder] = useState("");
  const [notificationState, setNotificationState] = useState(() => {
    if (!("Notification" in window)) return "unsupported";
    return reminderNotificationsEnabled() ? "enabled" : Notification.permission;
  });
  const [pushConfigured, setPushConfigured] = useState(null);
  const [pushConfig, setPushConfig] = useState(null);
  const [pushConfigError, setPushConfigError] = useState(false);
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [morning, setMorning] = useState({ enabled: false, wake_time: "07:00", weekdays: [1, 2, 3, 4, 5, 6, 7], timezone: "Europe/Helsinki" });
  const [savedMorning, setSavedMorning] = useState(null);
  const [morningSaving, setMorningSaving] = useState(false);
  const morningChanged = savedMorning && (
    morning.enabled !== savedMorning.enabled || morning.wake_time !== savedMorning.wake_time ||
    JSON.stringify(morning.weekdays) !== JSON.stringify(savedMorning.weekdays)
  );
  const morningAvailable = hasNativeAlarm || notificationState === "enabled";
  const visibleReminders = [...(selected ? [selected] : []), ...(data || []).filter((row) => row.id !== targetId)]
    .filter((reminder) => !removedIds.has(reminder.id))
    .filter((reminder) => reminder.id === targetId || filter === "all" || (filter === "done" ? reminder.done : !reminder.done))
    .sort(reminderOrder);
  const emptyCopy = REMINDER_EMPTY_COPY[filter] || REMINDER_EMPTY_COPY.all;

  useEffect(() => {
    loadPushConfig();
    loadMorning();
  }, []);

  async function loadPushConfig() {
    setPushConfigError(false);
    try {
      const config = await getPushConfig();
      setPushConfig(config); setPushConfigured(config.enabled);
      setPrivateContent(config.private_content !== false);
      localStorage.setItem(PRIVATE_NOTIFICATIONS_KEY, String(config.private_content !== false));
    } catch { setPushConfig(null); setPushConfigured(null); setPushConfigError(true); }
  }

  async function loadMorning() {
    setMorningLoad("loading");
    try {
      const preferences = await getMorningBriefingPreferences();
      const settings = { ...preferences, timezone: preferences.timezone || "Europe/Helsinki" };
      setMorning(settings); setSavedMorning(settings); setMorningLoad("ready");
    }
    catch { setMorningLoad("error"); }
  }
  useEffect(() => {
    if (!selectedId) return;
    const target = document.getElementById("reminder-card-" + selectedId);
    target?.scrollIntoView?.({ block: "center" }); target?.focus();
  }, [selectedId]);

  const toggle = async (r) => {
    setUpdatingReminder(r.id);
    try {
      await toggleReminder(r.id, !r.done);
      await refreshReminders();
      toast.success(r.done ? "Muistutus palautettiin avoimeksi." : "Muistutus kuitattiin hoidetuksi.", {
        action: {
          label: "Kumoa",
          onClick: async () => {
            try {
              await toggleReminder(r.id, r.done);
              await refreshReminders();
              toast.success("Muutos kumottu.");
            } catch {
              toast.error("Muutoksen kumoaminen epäonnistui.");
            }
          },
        },
      });
    } catch (requestError) {
      toast.error(userError(requestError, "Muistutuksen päivittäminen epäonnistui."));
    } finally {
      setUpdatingReminder("");
    }
  };
  const remove = async (id) => {
    await deleteReminder(id);
    setRemovedIds((previous) => new Set([...previous, id]));
    setData((previous) => previous?.filter((row) => row.id !== id) ?? previous);
    if (id === targetId) selection.setData(null);
    toast.success("Muistutus poistettu.");
    await refetch();
  };
  const enableNotifications = async () => {
    if (!pushConfig || pushConfigError || notificationSaving) return;
    setNotificationSaving(true);
    try {
    const permission = await requestReminderNotifications(pushConfig);
    setNotificationState(permission === "granted" ? "enabled" : permission);
    if (permission === "granted") toast.success("Puhelimen ilmoitukset otettiin käyttöön.");
    else if (permission === "denied") toast.error("Ilmoitukset on estetty selaimen asetuksissa.");
    else if (permission === "unsupported") toast.error("Tämä selain ei tue ilmoituksia.");
    else if (permission === "install_required") toast.error("Lisää ACAI ensin iPhonen Koti-valikkoon ja avaa se sieltä.");
    else if (permission === "unconfigured") toast.error("Push-palvelun käyttöönotto on vielä kesken.");
    } catch { toast.error("Ilmoitusten käyttöönottoa ei voitu vahvistaa. Yritä uudelleen."); }
    finally { setNotificationSaving(false); }
  };
  const disableNotifications = async () => {
    try {
      await disableReminderNotifications();
      setNotificationState(Notification.permission);
      toast.success("ACAI-ilmoitukset poistettiin käytöstä tältä laitteelta. Muiden laitteiden ilmoitukset säilyvät.");
    } catch {
      toast.error("Ilmoitusten poistaminen ei onnistunut.");
    }
  };
  const saveMorning = async () => {
    if (morningLoad !== "ready" || morningSaving) return;
    if (morning.weekdays.length === 0) {
      toast.error("Valitse vähintään yksi viikonpäivä.");
      return;
    }
    if (morning.enabled && !hasNativeAlarm && !reminderNotificationsEnabled()) {
      toast.error("Ota ensin puhelimen ilmoitukset käyttöön.");
      return;
    }
    setMorningSaving(true);
    try {
      const saved = await updateMorningBriefingPreferences(morning);
      if (hasNativeAlarm) {
        try {
          await syncNativeMorningAlarm(saved);
        } catch (alarmError) {
          if (saved.enabled) {
            const disabled = await updateMorningBriefingPreferences({ ...saved, enabled: false });
            setSavedMorning(disabled);
            setMorning({
              enabled: false,
              wake_time: disabled.wake_time,
              weekdays: disabled.weekdays,
              timezone: "Europe/Helsinki",
            });
          }
          throw alarmError;
        }
      }
      setMorning({
        enabled: !!saved.enabled,
        wake_time: saved.wake_time,
        weekdays: saved.weekdays,
        timezone: "Europe/Helsinki",
      });
      setSavedMorning(saved);
      toast.success(saved.enabled
        ? `${hasNativeAlarm ? "Herätys" : "Aamuilmoitus"} asetettu klo ${saved.wake_time}.`
        : `${hasNativeAlarm ? "Herätys" : "Aamuilmoitus"} poistettiin käytöstä.`);
    } catch (error) {
      toast.error(error?.message || "Aamukatsauksen tallentaminen ei onnistunut.");
    } finally {
      setMorningSaving(false);
    }
  };

  return (
    <div>
      <PageHeader icon={Bell} title="Muistutukset" subtitle="Kertaluontoiset ja toistuvat."
        action={<NewReminderDialog onCreated={refetch} />} />

      <div className="mb-4 flex gap-2 overflow-x-auto hide-scrollbar pb-1" data-testid="reminder-filters">
        {REMINDER_FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            data-testid={`reminder-filter-${item.id}`}
            onClick={() => setFilter(item.id)}
            aria-pressed={filter === item.id}
            className={cn(
              "whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
              filter === item.id
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground",
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {selection.present && <section aria-label="Linkistä avattu muistutus" className="mb-3 rounded-xl border p-3">
        {removedIds.has(targetId) ? <p role="status">Linkin muistutus on poistettu.</p> : selection.loading ? <p role="status">Linkin muistutusta haetaan…</p> : selection.error ? <>
          <p role="status">Linkin muistutusta ei voitu avata. Se on voitu poistaa, käyttöoikeus puuttuu tai yhteys ei toimi.</p>
          <button type="button" className="min-h-11 underline" onClick={selection.refetch}>Yritä avata muistutus uudelleen</button>
        </> : <p>Linkistä avattu muistutus näkyy alla. Avaaminen ei muuta sen tietoja.</p>}
      </section>}
      <RefreshNotice refreshing={refreshing} error={data || selected ? error : null} updatedAt={updatedAt} onRetry={refetch} />
      {loading && !selected ? <LoadingCards /> : error && !data && !selected ? <ErrorState error={error} onRetry={refetch} /> :
        visibleReminders.length === 0 ? <EmptyState icon={Bell} title={emptyCopy[0]} hint={emptyCopy[1]} /> : (
          <div className="space-y-3" data-testid="reminders-list">
            {visibleReminders.map((r) => {
              const timing = reminderState(r);
              return (
              <div id={"reminder-card-" + r.id} tabIndex={-1} key={r.id} className={cn("flex flex-wrap items-center gap-3 rounded-2xl border bg-card p-3 fade-up", timing === "overdue" ? "border-rose-500/40" : timing === "today" ? "border-amber-500/35" : "border-border")} data-testid={`reminder-${r.id}`}>
                <button type="button" data-testid={`reminder-toggle-${r.id}`} onClick={() => toggle(r)} disabled={updatingReminder === r.id}
                  aria-pressed={r.done}
                  aria-label={r.done ? `Palauta muistutus ${r.title} avoimeksi` : `Merkitse muistutus ${r.title} hoidetuksi`}
                  className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                    r.done ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40",
                    updatingReminder === r.id && "opacity-50")}>
                  {r.done && <Check className="h-4 w-4" />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className={cn("font-medium text-foreground", r.done && "line-through text-muted-foreground")}>{r.title}</p>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mt-0.5">
                    <span>{fiDateTime(r.remind_at)}</span>
                    {r.recurrence !== "none" && (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-muted px-2 py-0.5"><Repeat className="h-3 w-3" /> {RECURRENCE[r.recurrence]}</span>
                    )}
                    {timing === "overdue" && <span className="font-semibold text-rose-400">Myöhässä</span>}
                    {timing === "today" && <span className="font-semibold text-amber-400">Tänään</span>}
                  </div>
                </div>
                <div className="flex basis-full items-center justify-end sm:basis-auto">
                  <EditItemDialog item={r} kind="reminder" onUpdated={refreshReminders} disabled={updatingReminder === r.id} />
                  <ConfirmDeleteButton
                    testid={`reminder-delete-${r.id}`}
                    ariaLabel={`Poista muistutus ${r.title}`}
                    title="Poistetaanko muistutus?"
                    description={`Muistutus ”${r.title}” poistetaan pysyvästi. Tätä ei voi perua.`}
                    onConfirm={() => remove(r.id)}
                  />
                </div>
              </div>
            )})}
          </div>
        )}
      <details className="mt-5 rounded-2xl border border-border bg-card p-4">
        <summary className="min-h-11 cursor-pointer text-sm font-semibold">Ilmoitukset ja aamuyhteenveto · {morningLoad === "ready" ? morningChanged ? "Muutoksia tallentamatta" : morning.enabled ? "Aikataulu " + morning.wake_time : "Ei aamuilmoitusta" : "Asetusten tila tuntematon"}</summary>
        <p className="mb-3 text-xs text-muted-foreground">{notificationState === "enabled" ? "Tämän laitteen ilmoitukset käytössä." : "Tämän laitteen ilmoitukset eivät ole käytössä."}</p>
      <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-semibold text-foreground"><BellRing className="h-4 w-4 text-primary" /> Puhelimen ilmoitukset</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {notificationState === "enabled"
              ? "Käytössä myös sovelluksen ollessa suljettuna."
              : pushConfigured === false
                ? "Push-palvelun käyttöönotto on vielä kesken."
                : "iPhonessa lisää ACAI Koti-valikkoon ja salli sitten ilmoitukset."}
          </p>
        </div>
        {notificationState === "enabled" ? (
          <button type="button" onClick={disableNotifications} className="shrink-0 rounded-full border border-border px-3 py-2 text-xs font-semibold text-muted-foreground">Poista tältä laitteelta</button>
        ) : (
          <button type="button" onClick={enableNotifications} disabled={notificationSaving || notificationState === "unsupported" || !pushConfig || pushConfigError || pushConfigured === false} className="shrink-0 rounded-full bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-50">{notificationSaving ? "Otetaan käyttöön…" : "Ota käyttöön"}</button>
        )}
      </div>

      {pushConfigError && <p role="alert" className="mb-4 text-sm">Ilmoituspalvelun asetuksia ei voitu tarkistaa. <button type="button" onClick={loadPushConfig} className="min-h-11 underline">Tarkista ilmoituspalvelu uudelleen</button></p>}

      {morningLoad === "loading" && <p role="status">Aamuasetuksia haetaan…</p>}
      {morningLoad === "error" && <ErrorState onRetry={loadMorning} />}
      <fieldset disabled={morningLoad !== "ready" || morningSaving} className="mb-5 rounded-2xl border border-border bg-card p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-sm font-semibold text-foreground"><Sunrise className="h-4 w-4 text-primary" /> {hasNativeAlarm ? "Herätys ja aamuyhteenveto" : "Aamuilmoitus ja yhteenveto"}</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {hasNativeAlarm
                ? "ACAI herättää valitsemaasi aikaan. Avaa herätyksestä aamuyhteenveto, niin kuulet päivän tapahtumat, tehtävät, muistutukset ja tärkeät sähköpostit."
                : "Saat ilmoituksen heräämisaikaan. Kun avaat sen, ACAI kertoo päivän tapahtumat, tehtävät, muistutukset ja tärkeät sähköpostit."}
            </p>
          </div>
          <Switch
            checked={morning.enabled}
            onCheckedChange={(enabled) => setMorning((current) => ({ ...current, enabled }))}
            aria-label="Aamukatsaus käytössä"
            disabled={!morningAvailable && !morning.enabled}
          />
        </div>
        <fieldset className="mt-4" disabled={!morningAvailable || !morning.enabled}>
          <legend className="text-xs text-muted-foreground">Toistuu</legend>
          <div className="mt-2 grid grid-cols-7 gap-1.5">
            {WEEKDAYS.map((day) => {
              const selected = morning.weekdays.includes(day.value);
              return (
                <button
                  key={day.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setMorning((current) => ({
                    ...current,
                    weekdays: selected && current.weekdays.length > 1
                      ? current.weekdays.filter((value) => value !== day.value)
                      : selected
                        ? current.weekdays
                        : [...current.weekdays, day.value].sort(),
                  }))}
                  className={cn(
                    "rounded-full border px-1 py-2 text-xs font-semibold transition-colors",
                    selected ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground",
                  )}
                >
                  {day.label}
                </button>
              );
            })}
          </div>
        </fieldset>
        <div className="mt-4 flex items-end gap-3">
          <div className="flex-1">
            <Label htmlFor="wake-time" className="text-xs">{hasNativeAlarm ? "Heräämisaika" : "Aamuilmoituksen aika"}</Label>
            <Input
              id="wake-time"
              type="time"
              disabled={!morningAvailable || !morning.enabled}
              value={morning.wake_time}
              onChange={(event) => {
                const wakeTime = event.target.value;
                setMorning((current) => ({ ...current, wake_time: wakeTime }));
              }}
              className="mt-1 rounded-xl"
            />
          </div>
          <button
            type="button"
            onClick={saveMorning}
            disabled={morningSaving || (morning.enabled && !morningAvailable) || morningLoad !== "ready"}
            className="rounded-full bg-primary px-4 py-2.5 text-xs font-semibold text-primary-foreground disabled:opacity-50"
          >
            {morningSaving ? "Tallennetaan…" : "Tallenna"}
          </button>
        </div>
        {morningChanged && <p role="status" className="mt-3 text-xs text-amber-300">Muutoksen tallennusta ei ole vahvistettu. {savedMorning.enabled ? `Viimeksi vahvistettu asetus: aamuilmoitus klo ${savedMorning.wake_time}.` : "Viimeksi vahvistettu asetus: aamuilmoitus pois käytöstä."} Paina Tallenna vahvistaaksesi muutoksen.</p>}
        <p className="mt-3 text-[11px] text-muted-foreground">
          {hasNativeAlarm
            ? "Aikavyöhyke: Suomi. AlarmKit-herätys toimii myös hiljaisessa tilassa ja Keskity-tilan aikana."
            : "Aikavyöhyke: Suomi. Tämä on iPhonen järjestelmääntä käyttävä ilmoitus, ei hiljaisen tilan ohittava herätyskello."}
        </p>
        {!morningAvailable && <p className="mt-2 rounded-xl border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-xs font-medium text-amber-300">Tämän laitteen ilmoitukset eivät ole käytössä. Voit silti poistaa tilisi aamuilmoituksen käytöstä: kytke aamukatsaus pois ja paina Tallenna. Uuden aikataulun käyttöönotto vaatii ilmoitukset.</p>}
      </fieldset>

        <label className="flex min-h-11 items-start gap-3 text-sm"><input type="checkbox" checked={privateContent} disabled={privacySaving || !pushConfig || pushConfigError} onChange={async (event) => { const next = event.target.checked; setPrivacySaving(true); try { await updateNotificationPrivacy(next); setPrivateContent(next); localStorage.setItem(PRIVATE_NOTIFICATIONS_KEY, String(next)); } catch { toast.error("Ilmoituksen yksityisyysasetusta ei voitu tallentaa."); } finally { setPrivacySaving(false); } }} /> Piilota ilmoitusten sisältö (muistutukset ja aamuyhteenveto)</label>
        <p className="text-xs text-muted-foreground">Yksityinen ilmoitus kertoo vain uudesta ilmoituksesta. Tarkat tiedot avataan kirjautuneessa sovelluksessa. Puhelimen omat esikatseluasetukset vaikuttavat lisäksi näkyvyyteen.</p>
      </details>
    </div>
  );
}
